import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as solidIcons from '@fortawesome/free-solid-svg-icons';
import { useBusinessData } from '../../context/BusinessContext';
import BusinessInfo from '../common/BusinessInfo';
import HiringSection from '../common/HiringSection';
import '../../css/About.css';

// Get reference to specific icons we use directly in the code
const { faCheck, faUsers } = solidIcons;

// Icon mapping for core values - this will now work with any icon name from the JSON
const iconMapping = (iconName) => {
  return iconName && solidIcons[iconName] ? solidIcons[iconName] : solidIcons.faUsers;
};

const About = () => {
  const { loading, error, getData, hasData, getImageUrl } = useBusinessData();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">Error loading data: {error}</div>;
  }

  const coreValues = getData('about.coreValues', []);
  const leadershipTeam = getData('about.leadershipTeam', []);
  const statistics = getData('statistics', []);
  
  // Helper function to find statistics by title (case-insensitive)
  const getStatByTitle = (title) => {
    const stat = statistics.find(stat => 
      stat.title.toLowerCase().includes(title.toLowerCase())
    );
    return stat ? stat.value : null;
  };
  
  // Get about hero background image from JSON using getImageUrl
  const aboutHeroImage = getImageUrl('images.backgrounds.about', 'about-hero.jpg');
  
  // Get content image
  const aboutContentImage = getImageUrl('images.content.about', 'about-img.jpg');
  
  // Create inline style for background image - using URL directly
  const aboutHeroStyle = {
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('${aboutHeroImage}')`
  };

  // Helper function to get team member photos - simplified for external URLs
  const getTeamMemberPhoto = (member) => {
    if (!member || !member.photo) return null;
    
    // If photo is a full URL (starts with http), use it directly
    if (member.photo.startsWith('http')) {
      return member.photo;
    }
    
    // For role-based images from the images.team object
    const role = member.position.toLowerCase().replace(/\s+/g, '');
    if (role.includes('ceo') || role.includes('founder')) {
      return getImageUrl('images.team.ceo', member.photo);
    } else if (role.includes('cto')) {
      return getImageUrl('images.team.cto', member.photo);
    } else if (role.includes('coo')) {
      return getImageUrl('images.team.coo', member.photo);
    }
    
    // Default fallback
    return member.photo;
  };

  return (
    <>
      {/* About Hero */}
      <div className="about-hero" style={aboutHeroStyle}>
        <div className="container">
          <div className="about-hero-content">
            <h1>{getData('ui.about.pageTitle', 'About Us')}</h1>
            <p>{getData('ui.about.pageSubtitle', 'Learn more about our company and what drives us forward')}</p>
          </div>
        </div>
      </div>
      
      {/* About Content */}
      <section className="section">
        <div className="container">
          <div className="about-content">

            <div className="about-image">
              <img 
                src={aboutContentImage}
                alt="About Business Info" 
              />
            </div>
            <ul className="about-list">
                {statistics.map((stat, index) => (
                  <li key={index}>
                    <FontAwesomeIcon icon={faCheck} className="list-icon" /> 
                    {stat.value} {stat.title}
                  </li>
                ))}
              </ul>

          </div>
          <div className="about-info">

              <div className="about-text">
                <h2>{getData('ui.about.storySection.title', 'Our Story')}</h2>
                <p>{getData('about.summary', '')}</p>
                <p>{getData('about.journey', '')}</p>           
              </div>
            </div>
        </div>
      </section>
      
      {/* Values Section */}
      {hasData('about.coreValues') && coreValues.length > 0 && (
        <section className="section section-dark values-section">
          <div className="container">
            <h2 className="section-title">{getData('ui.about.valuesSection.title', 'Our Core Values')}</h2>
            <p className="section-subtitle">{getData('ui.about.valuesSection.subtitle', 'These principles guide everything we do and define who we are as a company.')}</p>
            
            <div className="values-grid">
              {coreValues.map((value, index) => (
                <div className="value-card" key={index}>
                  <FontAwesomeIcon 
                    icon={iconMapping(value.icon) || faUsers} 
                    className="value-icon" 
                  />
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* Team Section */}
      {hasData('about.leadershipTeam') && leadershipTeam.length > 0 && (
        <section className="section team-section">
          <div className="container">
            <h2 className="section-title">{getData('ui.about.teamSection.title', 'Our Leadership Team')}</h2>
            <p className="section-subtitle">{getData('ui.about.teamSection.subtitle', 'Meet the experts who drive our company forward.')}</p>
            
            <div className="team-grid">
              {leadershipTeam.map((member, index) => (
                <div className="team-member" key={index}>
                  <div className="member-img">
                    <img 
                      src={getTeamMemberPhoto(member)}
                      alt={member.name} 
                    />
                  </div>
                  <h3>{member.name}</h3>
                  <p className="member-position">{member.position}</p>
                  <p>{member.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* Business Legal Information Section */}
      <section className="section">
        <div className="container">
          <BusinessInfo />
        </div>
      </section>
      
      {/* Hiring Section */}
      {hasData('careers') && getData('careers.enabled') !== false && (
        <HiringSection />
      )}
    </>
  );
};

export default About; 